const STORAGE_KEY = "footage_attribution"

// Raw click IDs are stored separately so we don't break the existing
// `getAttribution()` shape that downstream code already consumes.
const TOUCH_KEY = "footage_attribution_touch"

const SEARCH_ENGINES = ["google", "bing", "yahoo", "duckduckgo", "baidu", "yandex"]
const SOCIAL_NETWORKS = ["facebook", "instagram", "twitter", "linkedin", "tiktok", "pinterest", "reddit", "youtube", "x.com", "t.co"]

function computeChannel(params: {
  utmSource?: string
  utmMedium?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
  referrer?: string
}): string {
  const { utmSource, utmMedium, gclid, fbclid, msclkid, referrer } = params
  const medium = utmMedium?.toLowerCase() ?? ""
  const source = utmSource?.toLowerCase() ?? ""

  // Paid click IDs are strongest signal
  if (gclid || msclkid) return "paid_search"
  if (fbclid) return "paid_social"

  // UTM medium-based classification
  if (medium === "cpc" || medium === "ppc" || medium === "paid" || medium === "paidsearch") return "paid_search"
  if (medium === "paid_social" || medium === "paidsocial") return "paid_social"
  if (medium === "email") return "email"
  if (medium === "organic" || medium === "organic_search") return "organic_search"
  if (medium === "social" || medium === "organic_social") return "organic_social"
  if (medium === "referral") return "referral"

  // Source-based classification when medium is missing
  if (source && SEARCH_ENGINES.some((e) => source.includes(e))) return "organic_search"
  if (source && SOCIAL_NETWORKS.some((s) => source.includes(s))) return "organic_social"
  if (source) return "referral"  // UTM source present but unrecognized → referral

  // Referrer-based classification when no UTM params
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase()
      if (SEARCH_ENGINES.some((e) => host.includes(e))) return "organic_search"
      if (SOCIAL_NETWORKS.some((s) => host.includes(s))) return "organic_social"
      return "referral"
    } catch {
      // invalid referrer URL
    }
  }

  return "direct"
}

/**
 * Capture attribution data from the current URL on first visit.
 * Call this before React renders to grab params before OAuth redirect strips them.
 */
export function captureAttribution(): void {
  try {
    // Only capture once — don't overwrite on OAuth callback
    if (localStorage.getItem(STORAGE_KEY)) return

    const params = new URLSearchParams(window.location.search)
    const utmSource = params.get("utm_source") ?? undefined
    const utmMedium = params.get("utm_medium") ?? undefined
    const utmCampaign = params.get("utm_campaign") ?? undefined
    const utmTerm = params.get("utm_term") ?? undefined
    const utmContent = params.get("utm_content") ?? undefined
    const gclid = params.get("gclid") ?? undefined
    const fbclid = params.get("fbclid") ?? undefined
    const msclkid = params.get("msclkid") ?? undefined
    const referrer = document.referrer || undefined
    const externalReferrer = referrer && !referrer.includes(window.location.hostname) ? referrer : undefined

    const channel = computeChannel({ utmSource, utmMedium, gclid, fbclid, msclkid, referrer: externalReferrer })

    // Store the actual referring domain as the referrer
    let source: string | undefined
    if (externalReferrer) {
      try { source = new URL(externalReferrer).hostname } catch {}
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify({ channel, referrer: source ?? "none" }))

    // Also store the raw touch with click IDs — needed server-side for
    // Google Ads conversion uploads (gclid), FB Conversions API (fbclid), etc.
    localStorage.setItem(TOUCH_KEY, JSON.stringify({
      utmSource, utmMedium, utmCampaign, utmTerm, utmContent,
      gclid, fbclid, msclkid,
      referrer: externalReferrer,
      landingPage: window.location.pathname + window.location.search,
      channel,
      timestamp: Date.now(),
    }))
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Read the full raw attribution touch (click IDs, UTM params, etc.) for
 * passing to the backend on signup. Returns null if nothing was captured.
 */
export interface AttributionTouch {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
  referrer?: string
  landingPage?: string
  channel?: string
  timestamp?: number
}

export function getAttributionTouch(): AttributionTouch | null {
  try {
    const raw = localStorage.getItem(TOUCH_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AttributionTouch
  } catch {
    return null
  }
}

/**
 * Read stored attribution data for sending to the backend.
 */
export function getAttribution(): { channel: string; referrer: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    return { channel: data.channel, referrer: data.referrer ?? "none" }
  } catch {
    return null
  }
}

/**
 * Clear attribution data from localStorage after it's been stored on the profile.
 */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(TOUCH_KEY)
  } catch {
    // ignore
  }
}
